<template>
    <Teleport to="body">
        <div v-if="open && message" class="edit-backdrop" @click.self="onClose">
            <div class="edit-dialog" role="dialog" aria-modal="true" aria-label="Редактирование ответа">
                <header class="edit-head">
                    <div>
                        <h2 class="edit-title">Редактирование ответа</h2>
                        <p class="edit-sub">
                            Измените формат графиков или допишите пожелания, затем нажмите
                            «Перегенерировать» — новый вариант ответа появится в этом окне.
                            В ленту чата он попадёт, когда вы нажмёте «Закрыть».
                        </p>
                    </div>
                    <button type="button" class="edit-close" aria-label="Закрыть" @click="onClose">
                        <ChatIcon name="x" :size="18" />
                    </button>
                </header>

                <div class="edit-body">
                    <div
                        v-if="message.status === 'streaming' && message.progress"
                        class="edit-progress"
                    >
                        <span class="edit-progress-dot" />
                        {{ message.progress }}
                    </div>

                    <ChatMessageSources
                        v-if="renderedSources.length"
                        :sources="renderedSources"
                    />

                    <div v-if="showText" class="preview-block">
                        <ChatAnswerText :text="message.text" :content="textContent" />
                    </div>

                    <ChatTypingIndicator
                        v-if="message.status === 'streaming' && !message.text && !renderedSources.length"
                    />

                    <template v-for="(card, idx) in message.cards" :key="`card-${idx}`">
                        <ChatAnswerCard
                            :card-type="card.cardType"
                            :payload="card.payload"
                            :view-config="card.viewConfig"
                            @update:view-config="onCardViewConfig(idx, $event)"
                        />
                    </template>

                    <template v-for="(list, idx) in message.lists" :key="`list-${idx}`">
                        <ChatNumberedList
                            :title="list.title"
                            :items="list.items"
                            :sources="renderedSources"
                        />
                    </template>

                    <template v-for="(callout, idx) in message.callouts" :key="`call-${idx}`">
                        <ChatInsightCallout
                            :tone="callout.tone"
                            :title="callout.title"
                            :content="callout.content"
                            :sources="renderedSources"
                        />
                    </template>

                    <p v-if="applyError" class="edit-apply-error" role="alert">{{ applyError }}</p>

                    <section v-if="layouts.length" class="edit-section">
                        <h3 class="edit-section-title">Шаблон ответа</h3>
                        <p class="edit-section-hint">
                            Перегенерировать в другом представлении. Можно выбрать несколько —
                            блоки сложатся в один ответ, как лист дашборда.
                        </p>
                        <div class="layout-grid">
                            <button
                                v-for="layout in layouts"
                                :key="layout.id"
                                type="button"
                                class="layout-card"
                                :class="{ 'layout-card--active': isLayoutSelected(layout.id) }"
                                :disabled="isBusy || isLayoutBlocked(layout.id)"
                                @click="toggleLayout(layout.id)"
                            >
                                <ChatLayoutSkeleton :kind="layout.skeleton || layout.id" />
                                <span class="layout-name">{{ layout.title }}</span>
                                <span class="layout-when">{{ layout.when_to_use || layout.subtitle }}</span>
                            </button>
                        </div>
                    </section>

                    <section v-if="chartEntries.length" class="edit-section">
                        <h3 class="edit-section-title">Формат графиков</h3>
                        <div
                            v-for="entry in chartEntries"
                            :key="entry.index"
                            class="chart-format-block"
                        >
                            <div class="chart-format-name">{{ entry.label }}</div>
                            <div class="format-row" role="group">
                                <button
                                    v-for="opt in formatOptions"
                                    :key="opt.id"
                                    type="button"
                                    class="chart-chip"
                                    :class="{ 'chart-chip--active': formatDraft[entry.index] === opt.id }"
                                    @click="formatDraft[entry.index] = opt.id"
                                >
                                    {{ opt.label }}
                                </button>
                            </div>
                        </div>
                    </section>

                    <section class="edit-section">
                        <h3 class="edit-section-title">Дополнительные пояснения</h3>
                        <p class="edit-section-hint">
                            Опишите, что изменить в ответе: акценты, срезы, пояснения к выводам.
                        </p>
                        <textarea
                            v-model="extraInstructions"
                            class="edit-textarea"
                            rows="4"
                            placeholder="Например: добавь сравнение с прошлым годом, сделай акцент на LADA…"
                            :disabled="isBusy"
                        />
                    </section>

                    <ChatMessageVersionNav
                        v-if="hasMultipleVersions"
                        :active="activeVersionIndex"
                        :total="versionTotal"
                        :label="activeVersionLabel"
                        :can-prev="activeVersionIndex > 0 && !isBusy"
                        :can-next="activeVersionIndex < versionTotal - 1 && !isBusy"
                        @prev="onVersionPrev"
                        @next="onVersionNext"
                    />
                </div>

                <footer class="edit-foot">
                    <p v-if="!isBusy && !hasChanges" class="edit-foot-hint">
                        Чтобы перегенерировать ответ, измените настройки выше.
                    </p>
                    <div class="edit-foot-actions">
                        <button type="button" class="btn-ghost" :disabled="isBusy" @click="onClose">
                            Закрыть
                        </button>
                        <button
                            type="button"
                            class="btn-primary"
                            :disabled="isBusy || !canRegenerate"
                            :title="regenerateButtonTitle"
                            @click="onApply"
                        >
                            {{ regenerateLabel }}
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatLayoutSkeleton from '@/components/chat/input/ChatLayoutSkeleton.vue'
import ChatMessageSources from './ChatMessageSources.vue'
import ChatAnswerText from './ChatAnswerText.vue'
import ChatAnswerCard from '@/components/chat/cards/ChatAnswerCard.vue'
import ChatNumberedList from './ChatNumberedList.vue'
import ChatInsightCallout from './ChatInsightCallout.vue'
import ChatTypingIndicator from './ChatTypingIndicator.vue'
import ChatMessageVersionNav from './ChatMessageVersionNav.vue'
import { CHARTABLE_TYPES, inferDefaultChartType } from '@/components/chat/cards/chartViewConfig'
import { REGEN_CHART_FORMAT_OPTIONS } from '@/components/chat/state/messageVersions'
import { getChatResponseFormatCatalog } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    message: { type: Object, default: null },
    isStreaming: { type: Boolean, default: false },
    applyError: { type: String, default: '' },
    // Шаблоны, действующие в чате: базовое значение для сравнения «есть изменения».
    responseFormats: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'apply', 'update-card-view', 'version-prev', 'version-next'])

const formatOptions = REGEN_CHART_FORMAT_OPTIONS
const formatDraft = ref({})
const baselineFormatDraft = ref({})
const extraInstructions = ref('')
const baselineExtra = ref('')
const layouts = ref([])
const defaultLayoutId = ref('auto')
const maxLayouts = ref(3)
const layoutDraft = ref([...props.responseFormats])
const baselineLayout = ref([...props.responseFormats])

const chartEntries = computed(() => {
    const cards = props.message?.cards || []
    return cards
        .map((card, index) => ({ card, index }))
        .filter(({ card }) => CHARTABLE_TYPES.has(card.cardType))
        .map(({ card, index }, chartableIndex) => ({
            index,
            chartableIndex,
            label: card.payload?.title || `График ${chartableIndex + 1}`,
        }))
})

const isBusy = computed(() =>
    props.message?.status === 'streaming' || props.isStreaming,
)

const hasFormatChanges = computed(() =>
    chartEntries.value.some(({ index }) =>
        (formatDraft.value[index] || 'line') !== (baselineFormatDraft.value[index] || 'line'),
    ),
)

const hasExtraChanges = computed(() =>
    extraInstructions.value.trim() !== baselineExtra.value,
)

const hasLayoutChanges = computed(
    () => layoutDraft.value.join(',') !== baselineLayout.value.join(','),
)

function isLayoutSelected(id) {
    return layoutDraft.value.includes(id)
}

function isLayoutBlocked(id) {
    return !isLayoutSelected(id)
        && id !== defaultLayoutId.value
        && layoutDraft.value.length >= maxLayouts.value
}

function toggleLayout(id) {
    // «Авто» — отсутствие шаблона, его карточка сбрасывает выбор.
    if (id === defaultLayoutId.value) {
        layoutDraft.value = []
        return
    }
    if (isLayoutSelected(id)) {
        layoutDraft.value = layoutDraft.value.filter(item => item !== id)
        return
    }
    if (isLayoutBlocked(id)) return

    const order = layouts.value.map(layout => layout.id)
    layoutDraft.value = [...layoutDraft.value, id]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
}

/** Подпись варианта в навигации по версиям: «Структура + Сводная таблица». */
function selectedLayoutLabel() {
    const titles = layoutDraft.value
        .map(id => layouts.value.find(layout => layout.id === id)?.title)
        .filter(Boolean)
    return titles.join(' + ')
}

const hasChanges = computed(
    () => hasFormatChanges.value || hasExtraChanges.value || hasLayoutChanges.value,
)

const canRegenerate = computed(() =>
    Boolean(props.message?.id) && hasChanges.value,
)

const regenerateLabel = computed(() => {
    if (isBusy.value) return 'Перегенерируем…'
    return 'Перегенерировать ответ'
})

const regenerateButtonTitle = computed(() => {
    if (isBusy.value) return ''
    if (!hasChanges.value) {
        return 'Сначала выберите шаблон, измените формат графика или допишите пожелания'
    }
    return 'Запустить перегенерацию с выбранными настройками'
})

const renderedSources = computed(() => {
    const m = props.message
    if (!m) return []
    if (Array.isArray(m.sources) && m.sources.length) return m.sources
    return m.source ? [m.source] : []
})

const showText = computed(() =>
    Boolean(props.message?.text || (props.message?.textContent && props.message.textContent.length)),
)

const textContent = computed(() => {
    if (props.message?.status === 'done' && props.message?.textContent) {
        return props.message.textContent
    }
    return null
})

const versionTotal = computed(() =>
    Array.isArray(props.message?.versions) ? props.message.versions.length : 0,
)

const hasMultipleVersions = computed(() => versionTotal.value > 1)

const activeVersionIndex = computed(() =>
    Number.isFinite(props.message?.activeVersionIndex)
        ? props.message.activeVersionIndex
        : 0,
)

const activeVersionLabel = computed(() => {
    const snap = props.message?.versions?.[activeVersionIndex.value]
    if (!snap) return ''
    if (activeVersionIndex.value === 0) return 'Исходный ответ'
    return snap.formatLabel || `Вариант ${activeVersionIndex.value + 1}`
})

function syncDraftFromMessage() {
    const cards = props.message?.cards || []
    const next = {}
    for (const { index } of chartEntries.value) {
        next[index] = inferDefaultChartType(cards[index])
    }
    formatDraft.value = next
}

function captureBaseline() {
    baselineFormatDraft.value = { ...formatDraft.value }
    baselineExtra.value = extraInstructions.value.trim()
    baselineLayout.value = [...layoutDraft.value]
}

onMounted(async () => {
    try {
        const data = await getChatResponseFormatCatalog()
        layouts.value = Array.isArray(data?.layouts) ? data.layouts : []
        if (typeof data?.default === 'string') defaultLayoutId.value = data.default
        if (Number.isFinite(data?.max_selected)) maxLayouts.value = data.max_selected
    } catch (error) {
        layouts.value = []
    }
})

watch(
    () => props.responseFormats,
    value => {
        if (isBusy.value) return
        layoutDraft.value = Array.isArray(value) ? [...value] : []
        baselineLayout.value = [...layoutDraft.value]
    },
)

watch(
    () => [
        props.open,
        props.message?.id,
        chartEntries.value.length,
        props.message?.activeVersionIndex,
        props.message?.status,
    ],
    () => {
        if (!props.open) return
        syncDraftFromMessage()
        if (!isBusy.value) {
            captureBaseline()
        }
    },
    { immediate: true },
)

watch(
    () => isBusy.value,
    (busy, wasBusy) => {
        if (!props.open) return
        if (wasBusy && !busy) {
            syncDraftFromMessage()
            extraInstructions.value = ''
            captureBaseline()
        }
    },
)

function onKeydown(event) {
    if (event.key === 'Escape' && props.open) onClose()
}

watch(
    () => props.open,
    isOpen => {
        if (typeof document === 'undefined') return
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            document.addEventListener('keydown', onKeydown)
        } else {
            document.body.style.overflow = ''
            document.removeEventListener('keydown', onKeydown)
            extraInstructions.value = ''
        }
    },
)

function onClose() {
    emit('close')
}

function onCardViewConfig(cardIndex, viewConfig) {
    emit('update-card-view', { messageId: props.message.id, cardIndex, viewConfig })
}

function onVersionPrev() {
    emit('version-prev', props.message.id)
}

function onVersionNext() {
    emit('version-next', props.message.id)
}

function onApply() {
    if (!canRegenerate.value) return
    const chartFormats = chartEntries.value.map(({ index, chartableIndex, label }) => ({
        cardIndex: index,
        chartableIndex,
        title: label,
        chartType: formatDraft.value[index] || 'line',
    }))
    emit('apply', {
        messageId: props.message.id,
        chartFormats,
        extraInstructions: extraInstructions.value,
        responseFormats: [...layoutDraft.value],
        // Метку шаблона отдаём только когда пользователь его действительно
        // сменил: иначе вариант подпишется «Авто» вместо формата графиков.
        responseFormatLabel: hasLayoutChanges.value ? selectedLayoutLabel() : '',
    })
    captureBaseline()
}
</script>

<style scoped>
.edit-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1300;
    background: rgba(21, 26, 34, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.edit-dialog {
    width: min(760px, 100%);
    max-height: calc(100vh - 40px);
    background: #fff;
    border-radius: 14px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 24px 48px rgba(21, 26, 34, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.edit-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 22px 14px;
    border-bottom: 1px solid #e7e7e7;
    flex-shrink: 0;
}

.edit-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #23282C;
}

.edit-sub {
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #888888;
    max-width: 520px;
}

.edit-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    cursor: pointer;
    flex-shrink: 0;
}

.edit-close:hover {
    background: #f0f0f0;
}

.edit-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 22px 20px;
}

.edit-progress {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #f0f0f0;
    border-radius: 8px;
    font-size: 12px;
    color: #5f5f5f;
}

.edit-progress-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #198FA0;
    animation: pulse 1.2s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}

.preview-block {
    margin-bottom: 8px;
}

.edit-apply-error {
    margin: 12px 0 0;
    padding: 10px 12px;
    border-radius: 8px;
    background: #FFF4EE;
    border: 1px solid #F2C4B8;
    font-size: 12px;
    line-height: 1.45;
    color: #8B2E1F;
}

.edit-section {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #e7e7e7;
}

.edit-section-title {
    margin: 0 0 10px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #888888;
}

.edit-section-hint {
    margin: 0 0 8px;
    font-size: 12px;
    color: #5f5f5f;
    line-height: 1.4;
}

.layout-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
}

.layout-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    background: white;
    border: 1px solid #e7e7e7;
    border-radius: 8px;
    padding: 8px;
    font-family: inherit;
    cursor: pointer;
}

.layout-card:hover:not(:disabled) {
    border-color: #c0c0c0;
    background: #fbfbfa;
}

.layout-card:disabled {
    opacity: 0.6;
    cursor: default;
}

.layout-card--active {
    border-color: #E30613;
    box-shadow: inset 0 0 0 1px #E30613;
}

.layout-name {
    font-size: 12px;
    font-weight: 600;
    color: #23282C;
}

.layout-when {
    font-size: 10.5px;
    line-height: 1.4;
    color: #888888;
}

.chart-format-block {
    margin-bottom: 14px;
}

.chart-format-block:last-child {
    margin-bottom: 0;
}

.chart-format-name {
    font-size: 12px;
    font-weight: 600;
    color: #23282C;
    margin-bottom: 8px;
}

.format-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.chart-chip {
    padding: 5px 11px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    font-family: inherit;
    font-size: 11.5px;
    font-weight: 600;
    color: #5f5f5f;
    cursor: pointer;
}

.chart-chip:hover {
    border-color: #c0c0c0;
    background: #f0f0f0;
    color: #23282C;
}

.chart-chip--active {
    border-color: #23282C;
    background: #23282C;
    color: white;
}

.edit-textarea {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    color: #23282C;
    resize: vertical;
    min-height: 88px;
}

.edit-textarea:focus {
    outline: none;
    border-color: #23282C;
}

.edit-textarea:disabled {
    background: #f7f7f7;
    opacity: 0.7;
}

.edit-foot {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 22px 18px;
    border-top: 1px solid #e7e7e7;
    flex-shrink: 0;
    background: #f7f7f7;
}

.edit-foot-hint {
    margin: 0;
    font-size: 11.5px;
    line-height: 1.4;
    color: #888888;
    text-align: right;
}

.edit-foot-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
}

.btn-ghost {
    padding: 9px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #5f5f5f;
    cursor: pointer;
}

.btn-ghost:hover:not(:disabled) {
    border-color: #c0c0c0;
    background: #f0f0f0;
}

.btn-primary {
    padding: 9px 18px;
    border: none;
    border-radius: 8px;
    background: #23282C;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: white;
    cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
    background: #3a4248;
}

.btn-primary:disabled,
.btn-ghost:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
